import React, { useEffect, useRef, useState } from 'react';
import { Wind, Play, Pause } from 'lucide-react';

const PHASES = [
  { key: 'inhale', label: 'Inhale', duration: 4 },
  { key: 'hold1', label: 'Hold', duration: 4 },
  { key: 'exhale', label: 'Exhale', duration: 4 },
  { key: 'hold2', label: 'Hold', duration: 4 },
];

const BoxBreathing = ({ embeddedFull = true }) => {
  const [running, setRunning] = useState(false);
  const [phaseIndex, setPhaseIndex] = useState(0);
  const [count, setCount] = useState(4);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!running) return () => {};
    timerRef.current = setInterval(() => {
      setCount((c) => {
        if (c > 1) return c - 1;
        setPhaseIndex((p) => (p + 1) % PHASES.length);
        return 4;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [running]);

  const phase = PHASES[phaseIndex];

  const scale = phase.key === 'inhale' ? 1.15 : phase.key === 'exhale' ? 0.85 : 1;

  const toggle = () => {
    if (!running) { setPhaseIndex(0); setCount(4); }
    setRunning(!running);
  };

  return (
    <section id="box-breathing" className={`scroll-mt-24 ${embeddedFull ? 'py-12 sm:py-20' : ''}`}>
      <div className={`${embeddedFull ? 'max-w-4xl mx-auto px-4' : ''}`}>
        <div className="rounded-3xl border border-emerald-100 bg-gradient-to-br from-emerald-50 to-white p-6 sm:p-10 text-center shadow-sm">
          <span className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-emerald-700">
            <Wind className="w-3.5 h-3.5" /> Box Breathing Companion
          </span>
          <h3 className="mt-1 font-serif text-2xl sm:text-3xl md:text-4xl text-emerald-950">4-4-4-4 Technique for Instant Anxiety Relief</h3>

          <div className="mt-8 flex items-center justify-center">
            <div
              className="relative w-56 h-56 sm:w-64 sm:h-64 rounded-3xl border-2 border-emerald-300 bg-white flex items-center justify-center transition-transform duration-1000 ease-in-out shadow-lg shadow-emerald-100"
              style={{ transform: `scale(${running ? scale : 1})` }}
            >
              <div className="text-center">
                <div className="text-[11px] uppercase tracking-[0.3em] text-emerald-700">{running ? phase.label : 'Tap Start'}</div>
                <div className="font-serif text-6xl sm:text-7xl text-emerald-900 mt-1">{count}</div>
              </div>
              <span className="absolute -top-1 -left-1 w-3 h-3 rounded-full bg-emerald-500 animate-pulse" />
              <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-emerald-400" />
              <span className="absolute -bottom-1 -left-1 w-3 h-3 rounded-full bg-emerald-400" />
              <span className="absolute -bottom-1 -right-1 w-3 h-3 rounded-full bg-emerald-500" />
            </div>
          </div>

          <div className="mt-6 flex items-center justify-center gap-3">
            <button onClick={toggle} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-emerald-700 text-white hover:bg-emerald-800 transition-colors shadow-md shadow-emerald-200">
              {running ? <><Pause className="w-4 h-4" /> Stop</> : <><Play className="w-4 h-4 fill-white" /> Start Exercise</>}
            </button>
          </div>

          <div className="mt-6 grid grid-cols-4 gap-2 max-w-md mx-auto">
            {PHASES.map((p, i) => (
              <div key={p.key + i} className={`rounded-lg p-2 text-xs ${i === phaseIndex && running ? 'bg-emerald-700 text-white' : 'bg-white border border-emerald-100 text-emerald-800'}`}>
                <div className="uppercase tracking-widest text-[10px] opacity-80">Step {i + 1}</div>
                <div className="font-medium">{p.label} · {p.duration}s</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default BoxBreathing;
